/**
 * System Blueprint - Structured Option Selection Model
 *
 * Refactors the option selection flow to:
 *   Heat Source → DHW Architecture → Cylinder Type/Class → Controls/Zoning → Distribution
 *
 * This is a new layer that sits IN FRONT of the existing scoring engine.
 * It captures a coherent system configuration and converts it into an
 * EvaluationContext that scoring functions consume.
 *
 * Design principles:
 * - No hard-coded scores. Only physics + defaults with provenance.
 * - Every default has a label, provenance, and is overrideable.
 * - Invalid combinations are structurally prevented.
 * - The blueprint is the single truth for what the proposed system IS.
 */

import type {
  SystemRecInput,
  RetrofitLevel,
  LowTempReadiness,
  EmitterProfile,
  RetrofitProfile,
  OccupancyTiming,
} from './types';

// ============================================================================
// PROVENANCE DEFAULTS
// ============================================================================

/**
 * Provenance tag for any default value used in the system.
 * Every assumed number must carry one of these so the UI can show
 * "where did this come from?" and let the user change it.
 */
export type DefaultProvenance = 'confirmed' | 'derived' | 'assumed';

/**
 * A default value with full traceability.
 *
 * Rule: No hard-coded scores.
 * Only hard-coded physics + defaults, and every default must have:
 *   - a label (what is this?)
 *   - a provenance (confirmed | derived | assumed)
 *   - a source (where does this come from?)
 */
export interface ProvenanceDefault<T> {
  /** The actual value */
  value: T;
  /** Human-readable label for display */
  label: string;
  /** How this value was determined */
  provenance: DefaultProvenance;
  /** Source reference (e.g., "SAP 10.2", "MCS data 2024", "user confirmed") */
  source: string;
  /** Whether the user can override this value */
  overrideable: boolean;
}

/**
 * Create a provenance-tracked default value.
 */
export function createDefault<T>(
  value: T,
  label: string,
  provenance: DefaultProvenance,
  source: string,
  overrideable = true
): ProvenanceDefault<T> {
  return { value, label, provenance, source, overrideable };
}

// ============================================================================
// SYSTEM BLUEPRINT TYPES
// ============================================================================

/** Step 1: Heat source selection */
export type HeatSource = 'gas_combi' | 'gas_system' | 'gas_regular' | 'gas_storage_combi' | 'ashp' | 'gshp' | 'hybrid';

/** Step 2: DHW architecture (depends on heat source) */
export type DHWArchitecture = 'combi' | 'stored';

/** Step 3: Cylinder pressure type (only if stored DHW) */
export type CylinderPressure = 'vented' | 'unvented';

/** Step 4: Cylinder class (standard vs Mixergy upgrade) */
export type CylinderClass = 'standard' | 'mixergy';

/** Cylinder retention decision */
export type CylinderRetention = 'replace' | 'retain';

/** Zoning topology */
export type ZoningType = 'yplan' | 'splan';

/**
 * The System Blueprint captures the chosen system structure
 * following the decision flow:
 *
 *   Heat Source → DHW Architecture → (if stored) Cylinder Pressure → Cylinder Class
 *   → Controls/Zoning → Distribution
 *
 * This prevents invalid combinations structurally.
 */
export interface SystemBlueprint {
  /** Step 1: Heat source */
  heatSource: HeatSource;

  /** Step 2 + 3 + 4: DHW configuration */
  dhw: DHWCombiConfig | DHWStoredConfig;

  /** Step 5: Controls and zoning */
  controls: {
    zoning: ZoningType;
    smart: boolean;
  };

  /** Step 6: Distribution */
  distribution: {
    /** Primary pipe size in mm (22 or 28) */
    primariesMm?: 22 | 28;
  };
}

/** DHW config for combi (no cylinder) */
export interface DHWCombiConfig {
  architecture: 'combi';
}

/** DHW config for stored hot water (has cylinder) */
export interface DHWStoredConfig {
  architecture: 'stored';
  cylinder: {
    pressure: CylinderPressure;
    clazz: CylinderClass;
    retention: CylinderRetention;
  };
}

/**
 * Type guard: is this a stored DHW configuration?
 */
export function isStoredDHW(dhw: DHWCombiConfig | DHWStoredConfig): dhw is DHWStoredConfig {
  return dhw.architecture === 'stored';
}

// ============================================================================
// EVALUATION CONTEXT
// ============================================================================

/**
 * EvaluationContext is the single "truth" that scoring functions consume.
 *
 * It is derived from:
 *   1. The SystemBlueprint (proposed system)
 *   2. The SystemRecInput (assessment/survey data)
 *
 * No scoring function should access raw assessment data directly.
 * Instead, everything flows through this context.
 */
export interface EvaluationContext {
  // --- Proposed system (from blueprint) ---
  /** Engine boiler type string (e.g., 'combi', 'system', 'air_source_hp') */
  boilerType: string;
  /** Engine water type string (e.g., 'on_demand', 'unvented', 'mixergy_unvented') */
  waterType: string;
  /** Is this a heat pump system? */
  isHeatPump: boolean;
  /** Is this a gas boiler system? */
  isGasBoiler: boolean;
  /** Does this system have a stored DHW cylinder? */
  hasStoredDHW: boolean;
  /** Is the cylinder unvented? */
  isUnvented: boolean;
  /** Is this a Mixergy cylinder? */
  isMixergy: boolean;
  /** Is the existing cylinder being retained? */
  retainCylinder: boolean;
  /** Zoning configuration */
  zoning: ZoningType;
  /** Smart controls present */
  smartControls: boolean;
  /** Primary pipe size */
  primariesMm: number | null;

  // --- Property context (from assessment) ---
  heatLossKw: ProvenanceDefault<number>;
  annualDemandKwh: ProvenanceDefault<number | null>;
  occupants: ProvenanceDefault<number>;
  bathrooms: ProvenanceDefault<number>;
  drawsPerDay: ProvenanceDefault<number>;
  propertyType: string;
  occupancyTiming: OccupancyTiming;

  // --- Mains water (from assessment) ---
  mainsPressureBar: ProvenanceDefault<number>;
  flowRateLpm: ProvenanceDefault<number>;

  // --- Envelope (from assessment) ---
  retrofitLevel: RetrofitLevel;
  lowTempReadiness: LowTempReadiness;
  emitterProfile: EmitterProfile;
  retrofitProfile: RetrofitProfile;

  // --- Cylinder performance defaults (from engineering data) ---
  standingLossKwhPerDay: ProvenanceDefault<number>;
  cylinderLifespanYears: ProvenanceDefault<number>;

  // --- Energy costs (from tariff data) ---
  gasUnitRatePPerKwh: ProvenanceDefault<number>;
  electricityUnitRatePPerKwh: ProvenanceDefault<number>;

  // --- Flags for "not assessed" tracking ---
  /** True when annual demand inputs are missing → running cost = "Not assessed" */
  runningCostAssessable: boolean;
  /** True when enough data exists for future-proofing scoring */
  futureProofingAssessable: boolean;

  // --- All assumptions used, for display ---
  assumptions: ProvenanceDefault<unknown>[];
}

// ============================================================================
// VALID COMBINATION RULES
// ============================================================================

/**
 * Returns valid DHW architectures for a given heat source.
 * This structurally prevents invalid combinations.
 */
export function getValidDHWArchitectures(heatSource: HeatSource): DHWArchitecture[] {
  switch (heatSource) {
    case 'gas_combi':
    case 'gas_storage_combi':
      return ['combi'];
    case 'gas_system':
    case 'gas_regular':
      return ['stored'];
    case 'ashp':
    case 'gshp':
      return ['stored']; // Heat pumps always need stored DHW
    case 'hybrid':
      return ['stored'];
    default:
      return ['stored'];
  }
}

/**
 * Returns valid cylinder pressure types for a given heat source + architecture.
 */
export function getValidCylinderPressures(
  heatSource: HeatSource,
  architecture: DHWArchitecture
): CylinderPressure[] {
  if (architecture === 'combi') return [];

  switch (heatSource) {
    case 'gas_system':
      return ['unvented', 'vented'];
    case 'gas_regular':
      return ['vented', 'unvented'];
    case 'ashp':
    case 'gshp':
    case 'hybrid':
      return ['unvented', 'vented']; // Unvented preferred for heat pumps
    default:
      return ['vented', 'unvented'];
  }
}

/**
 * Check whether a blueprint represents a valid system configuration.
 */
export function validateBlueprint(blueprint: SystemBlueprint): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate DHW architecture matches heat source constraints
  const validArchitectures = getValidDHWArchitectures(blueprint.heatSource);
  if (!validArchitectures.includes(blueprint.dhw.architecture)) {
    errors.push(
      `DHW architecture "${blueprint.dhw.architecture}" is not valid for heat source "${blueprint.heatSource}". ` +
      `Valid: ${validArchitectures.join(', ')}`
    );
  }

  // Validate cylinder config exists for stored DHW
  if (blueprint.dhw.architecture === 'stored') {
    const stored = blueprint.dhw as DHWStoredConfig;
    if (!stored.cylinder) {
      errors.push('Stored DHW requires cylinder configuration');
    }

    const validPressures = getValidCylinderPressures(blueprint.heatSource, 'stored');
    if (stored.cylinder && !validPressures.includes(stored.cylinder.pressure)) {
      errors.push(
        `Cylinder pressure "${stored.cylinder.pressure}" not valid for heat source "${blueprint.heatSource}". ` +
        `Valid: ${validPressures.join(', ')}`
      );
    }
  }

  // Validate combi doesn't have cylinder config
  if (blueprint.dhw.architecture === 'combi') {
    if ('cylinder' in blueprint.dhw && (blueprint.dhw as any).cylinder) {
      errors.push('Combi DHW should not have cylinder configuration');
    }
  }

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// ENGINEERING DEFAULTS (physics-based, with provenance)
// ============================================================================

/**
 * Standing loss defaults by cylinder type.
 * Source: SAP 10.2 Table 2a, manufacturer data sheets.
 */
export const CYLINDER_STANDING_LOSS_DEFAULTS: Record<string, ProvenanceDefault<number>> = {
  'vented_standard': createDefault(
    1.8, 'Modern open-vented cylinder standing loss',
    'derived', 'SAP 10.2 Table 2a — 120L factory-insulated open vented'
  ),
  'vented_mixergy': createDefault(
    1.1, 'Mixergy open-vented cylinder standing loss',
    'derived', 'Mixergy datasheet — stratified design reduces effective loss'
  ),
  'unvented_standard': createDefault(
    1.5, 'Modern unvented cylinder standing loss',
    'derived', 'SAP 10.2 Table 2a — 180L factory-insulated unvented'
  ),
  'unvented_mixergy': createDefault(
    0.9, 'Mixergy unvented cylinder standing loss',
    'derived', 'Mixergy datasheet — stratified + higher insulation grade'
  ),
};

/**
 * Cylinder lifespan defaults by type.
 * Source: Industry data, warranty periods, reliability surveys.
 */
export const CYLINDER_LIFESPAN_DEFAULTS: Record<string, ProvenanceDefault<number>> = {
  'vented_standard': createDefault(18, 'Open vented cylinder lifespan', 'derived', 'Industry average — copper indirect cylinder'),
  'vented_mixergy': createDefault(22, 'Mixergy open-vented cylinder lifespan', 'derived', 'Mixergy warranty data'),
  'unvented_standard': createDefault(25, 'Unvented cylinder lifespan', 'derived', 'Manufacturer warranty — stainless steel unvented'),
  'unvented_mixergy': createDefault(22, 'Mixergy unvented cylinder lifespan', 'derived', 'Mixergy warranty data'),
  'none': createDefault(0, 'No cylinder (combi)', 'confirmed', 'No cylinder in system'),
};

/**
 * Energy price defaults.
 * Source: Ofgem Q1 2026 price cap.
 */
export const ENERGY_PRICE_DEFAULTS = {
  gas: createDefault(5.93, 'Gas unit rate', 'derived', 'Ofgem Q1 2026 price cap'),
  electricity: createDefault(27.69, 'Electricity unit rate', 'derived', 'Ofgem Q1 2026 price cap'),
} as const;

/**
 * Modern condensing boiler seasonal efficiency default range.
 * Source: SEDBUK database typical A-rated boiler range.
 */
export const BOILER_EFFICIENCY_DEFAULTS = {
  modernCondensing: createDefault(
    { min: 0.90, max: 0.94 },
    'Modern condensing boiler seasonal efficiency range',
    'derived',
    'SEDBUK A-band typical range'
  ),
} as const;

// ============================================================================
// BLUEPRINT → ENGINE TYPE MAPPING
// ============================================================================

/**
 * Maps a blueprint heat source to the engine's boiler type string.
 */
export function blueprintHeatSourceToBoilerType(heatSource: HeatSource): string {
  switch (heatSource) {
    case 'gas_combi': return 'combi';
    case 'gas_system': return 'system';
    case 'gas_regular': return 'regular';
    case 'gas_storage_combi': return 'storage_combi';
    case 'ashp': return 'air_source_hp';
    case 'gshp': return 'ground_source_hp';
    case 'hybrid': return 'hybrid';
  }
}

/**
 * Maps a blueprint DHW configuration to the engine's water type string.
 */
export function blueprintDHWToWaterType(
  heatSource: HeatSource,
  dhw: DHWCombiConfig | DHWStoredConfig
): string {
  if (dhw.architecture === 'combi') {
    if (heatSource === 'gas_storage_combi') return 'storage_combi';
    return 'on_demand';
  }

  const stored = dhw as DHWStoredConfig;
  const { pressure, clazz } = stored.cylinder;

  if (clazz === 'mixergy') {
    return pressure === 'unvented' ? 'mixergy_unvented' : 'mixergy_open';
  }

  return pressure === 'unvented' ? 'unvented' : 'open_vented';
}

// ============================================================================
// BUILD EVALUATION CONTEXT
// ============================================================================

/**
 * Build an EvaluationContext from a SystemBlueprint and assessment data.
 *
 * This is the bridge between the structured blueprint selection and
 * the existing scoring engine. All scoring functions should consume
 * EvaluationContext rather than accessing raw assessment data.
 */
export function buildEvaluationContext(
  blueprint: SystemBlueprint,
  assessment: SystemRecInput
): EvaluationContext {
  // Validate blueprint first
  const validation = validateBlueprint(blueprint);
  if (!validation.valid) {
    throw new Error(`Invalid blueprint: ${validation.errors.join('; ')}`);
  }

  // Map blueprint to engine types
  const boilerType = blueprintHeatSourceToBoilerType(blueprint.heatSource);
  const waterType = blueprintDHWToWaterType(blueprint.heatSource, blueprint.dhw);

  const isHeatPump = blueprint.heatSource === 'ashp' || blueprint.heatSource === 'gshp';
  const isGasBoiler = ['gas_combi', 'gas_system', 'gas_regular', 'gas_storage_combi'].includes(blueprint.heatSource);
  const hasStoredDHW = isStoredDHW(blueprint.dhw);

  const storedDHW = hasStoredDHW ? (blueprint.dhw as DHWStoredConfig) : null;
  const isUnvented = storedDHW?.cylinder.pressure === 'unvented';
  const isMixergy = storedDHW?.cylinder.clazz === 'mixergy';
  const retainCylinder = storedDHW?.cylinder.retention === 'retain';

  // Resolve cylinder standing loss
  const cylinderKey = !hasStoredDHW
    ? 'none'
    : `${storedDHW!.cylinder.pressure}_${storedDHW!.cylinder.clazz}`;

  const standingLossDefault = CYLINDER_STANDING_LOSS_DEFAULTS[cylinderKey]
    ?? createDefault(1.5, 'Default cylinder standing loss', 'assumed', 'Fallback — no specific data');

  const lifespanDefault = CYLINDER_LIFESPAN_DEFAULTS[cylinderKey]
    ?? createDefault(20, 'Default cylinder lifespan', 'assumed', 'Fallback — no specific data');

  // Resolve property data with provenance
  const occupants = assessment.occupancy !== undefined
    ? createDefault(assessment.occupancy, 'Occupants', 'confirmed', 'User entered')
    : assessment.bathrooms !== undefined
      ? createDefault(Math.round(assessment.bathrooms * 1.5), 'Occupants (estimated)', 'derived', 'Derived from bathrooms × 1.5')
      : createDefault(3, 'Occupants (default)', 'assumed', 'UK average household size');

  const bathrooms = assessment.bathrooms !== undefined
    ? createDefault(assessment.bathrooms, 'Bathrooms', 'confirmed', 'User entered')
    : assessment.occupancy !== undefined
      ? createDefault(Math.ceil(assessment.occupancy * 0.5), 'Bathrooms (estimated)', 'derived', 'Derived from occupants × 0.5')
      : createDefault(2, 'Bathrooms (default)', 'assumed', 'UK average');

  const drawsPerDay = assessment.drawsPerDay !== undefined
    ? createDefault(assessment.drawsPerDay, 'Hot water draws per day', 'confirmed', 'User entered')
    : createDefault(
        Math.round(occupants.value * 2 + bathrooms.value * 1.5 + 4),
        'Hot water draws per day (estimated)',
        'derived',
        'Derived: 2 per person + 1.5 per bathroom + 4 base'
      );

  // Resolve heat loss
  const knownHeatLoss = assessment.knownHeatLossKw ?? assessment.heatLossKw;
  const heatLossKw = knownHeatLoss !== undefined
    ? createDefault(knownHeatLoss, 'Heat loss', 'confirmed', 'User entered or calculated')
    : createDefault(10, 'Heat loss (default)', 'assumed', 'Assumed — no heat loss data provided');

  // Resolve annual demand
  const hasAnnualDemand = assessment.actualAnnualHeatKwh !== undefined;
  const annualDemandKwh = hasAnnualDemand
    ? createDefault<number | null>(assessment.actualAnnualHeatKwh!, 'Annual heat demand', 'confirmed', 'User entered from meter/bill')
    : createDefault<number | null>(null, 'Annual heat demand', 'assumed', 'Not provided — running cost will show "Not assessed"');

  // Resolve mains pressure and flow
  const mainsPressureBar = assessment.mainsPressure !== undefined
    ? createDefault(assessment.mainsPressure, 'Mains pressure', 'confirmed', 'User entered or measured')
    : assessment.mainsStaticPressureBar !== undefined
      ? createDefault(assessment.mainsStaticPressureBar, 'Mains static pressure', 'confirmed', 'User measured')
      : createDefault(2.0, 'Mains pressure (default)', 'assumed', 'UK typical mains pressure');

  const flowRateLpm = assessment.flowRate !== undefined
    ? createDefault(assessment.flowRate, 'Flow rate', 'confirmed', 'User entered or measured')
    : assessment.mainsDynamicFlowLpm !== undefined
      ? createDefault(assessment.mainsDynamicFlowLpm, 'Dynamic flow rate', 'confirmed', 'User measured')
      : createDefault(14, 'Flow rate (default)', 'assumed', 'UK typical mains flow rate');

  // Resolve retrofit metrics
  const retrofitLevel: RetrofitLevel = (assessment as any).derivedRetrofitLevel ?? 'medium';
  const lowTempReadiness: LowTempReadiness = (assessment as any).derivedLowTempReadiness ?? 'medium';

  const emitterProfile: EmitterProfile = assessment.emitters
    ? {
        type: assessment.emitters.type ?? 'unknown',
        lowTempSuitability: assessment.emitters.lowTempSuitability ?? 'unknown',
        roomsStruggleToHeat: assessment.emitters.roomsStruggleToHeat ?? 'unknown',
        pipeBore: assessment.emitters.pipeBore,
      }
    : { type: 'unknown', lowTempSuitability: 'unknown', roomsStruggleToHeat: 'unknown' };

  const retrofitProfile: RetrofitProfile = assessment.retrofit
    ? {
        loftInsulation: normalizeRetrofitValue(assessment.retrofit.loftInsulation),
        wallInsulation: normalizeWallInsulation(assessment.retrofit.wallInsulation),
        windows: normalizeWindowValue(assessment.retrofit.windows),
        draughtProofing: normalizeRetrofitValue(assessment.retrofit.draughtProofing),
      }
    : { loftInsulation: 'unknown', wallInsulation: 'unknown', windows: 'unknown', draughtProofing: 'unknown' };

  // Determine assessability flags
  const runningCostAssessable = annualDemandKwh.value !== null;
  const futureProofingAssessable = heatLossKw.provenance !== 'assumed' ||
    assessment.emitters?.lowTempSuitability !== undefined;

  // Collect all assumptions for display
  const assumptions: ProvenanceDefault<unknown>[] = [];
  if (occupants.provenance !== 'confirmed') assumptions.push(occupants);
  if (bathrooms.provenance !== 'confirmed') assumptions.push(bathrooms);
  if (drawsPerDay.provenance !== 'confirmed') assumptions.push(drawsPerDay);
  if (heatLossKw.provenance !== 'confirmed') assumptions.push(heatLossKw);
  if (mainsPressureBar.provenance !== 'confirmed') assumptions.push(mainsPressureBar);
  if (flowRateLpm.provenance !== 'confirmed') assumptions.push(flowRateLpm);
  if (standingLossDefault.provenance !== 'confirmed') assumptions.push(standingLossDefault);
  if (lifespanDefault.provenance !== 'confirmed') assumptions.push(lifespanDefault);

  return {
    // Proposed system
    boilerType,
    waterType,
    isHeatPump,
    isGasBoiler,
    hasStoredDHW,
    isUnvented,
    isMixergy,
    retainCylinder,
    zoning: blueprint.controls.zoning,
    smartControls: blueprint.controls.smart,
    primariesMm: blueprint.distribution.primariesMm ?? null,

    // Property context
    heatLossKw,
    annualDemandKwh,
    occupants,
    bathrooms,
    drawsPerDay,
    propertyType: assessment.propertyType ?? 'unknown',
    occupancyTiming: assessment.occupancyTiming ?? 'unknown',

    // Mains water
    mainsPressureBar,
    flowRateLpm,

    // Envelope
    retrofitLevel,
    lowTempReadiness,
    emitterProfile,
    retrofitProfile,

    // Cylinder defaults
    standingLossKwhPerDay: standingLossDefault,
    cylinderLifespanYears: lifespanDefault,

    // Energy costs
    gasUnitRatePPerKwh: ENERGY_PRICE_DEFAULTS.gas,
    electricityUnitRatePPerKwh: ENERGY_PRICE_DEFAULTS.electricity,

    // Assessability
    runningCostAssessable,
    futureProofingAssessable,

    // Assumptions
    assumptions,
  };
}

// ============================================================================
// BLUEPRINT BUILDERS (convenience constructors)
// ============================================================================

/**
 * Creates a SystemBlueprint from the legacy option config format.
 * This bridges the existing { boiler: string, water: string } option configs
 * into the structured blueprint model.
 */
export function blueprintFromLegacyConfig(
  boiler: string,
  water: string,
  overrides?: Partial<Pick<SystemBlueprint, 'controls' | 'distribution'>>
): SystemBlueprint {
  const heatSource = legacyBoilerToHeatSource(boiler);
  const dhw = legacyWaterToDHWConfig(water);

  return {
    heatSource,
    dhw,
    controls: overrides?.controls ?? { zoning: 'yplan', smart: false },
    distribution: overrides?.distribution ?? {},
  };
}

function legacyBoilerToHeatSource(boiler: string): HeatSource {
  switch (boiler) {
    case 'combi': return 'gas_combi';
    case 'system': return 'gas_system';
    case 'regular': return 'gas_regular';
    case 'storage_combi': return 'gas_storage_combi';
    case 'air_source_hp': return 'ashp';
    case 'ground_source_hp': return 'gshp';
    case 'hybrid': return 'hybrid';
    default: return 'gas_combi';
  }
}

function legacyWaterToDHWConfig(water: string): DHWCombiConfig | DHWStoredConfig {
  switch (water) {
    case 'on_demand':
    case 'storage_combi':
      return { architecture: 'combi' };

    case 'open_vented':
      return { architecture: 'stored', cylinder: { pressure: 'vented', clazz: 'standard', retention: 'replace' } };

    case 'unvented':
      return { architecture: 'stored', cylinder: { pressure: 'unvented', clazz: 'standard', retention: 'replace' } };

    case 'mixergy_open':
      return { architecture: 'stored', cylinder: { pressure: 'vented', clazz: 'mixergy', retention: 'replace' } };

    case 'mixergy_unvented':
      return { architecture: 'stored', cylinder: { pressure: 'unvented', clazz: 'mixergy', retention: 'replace' } };

    default:
      return { architecture: 'stored', cylinder: { pressure: 'unvented', clazz: 'standard', retention: 'replace' } };
  }
}

// ============================================================================
// OPTION BUILDER (generates valid option set from blueprint flow)
// ============================================================================

/**
 * Generate all valid system blueprints for a given assessment.
 * This replaces the hardcoded A-S option list with a principled expansion
 * that only produces valid combinations.
 */
export function generateValidBlueprints(
  assessment: SystemRecInput,
  options?: {
    includeMixergy?: boolean;
    includeHeatPumps?: boolean;
    maxOptions?: number;
  }
): SystemBlueprint[] {
  const blueprints: SystemBlueprint[] = [];
  const includeMixergy = options?.includeMixergy ?? true;
  const includeHeatPumps = options?.includeHeatPumps ?? true;
  const gasAvailable = assessment.gasAvailable !== false;

  // Determine available heat sources
  const heatSources: HeatSource[] = [];
  if (gasAvailable) {
    heatSources.push('gas_combi', 'gas_system', 'gas_regular', 'gas_storage_combi');
  }
  if (includeHeatPumps) {
    heatSources.push('ashp', 'gshp');
  }

  for (const heatSource of heatSources) {
    const validArchitectures = getValidDHWArchitectures(heatSource);

    for (const architecture of validArchitectures) {
      if (architecture === 'combi') {
        // Combi: no cylinder
        blueprints.push({
          heatSource,
          dhw: { architecture: 'combi' },
          controls: { zoning: 'yplan', smart: false },
          distribution: {},
        });
        continue;
      }

      // Stored DHW: iterate cylinder options
      const validPressures = getValidCylinderPressures(heatSource, architecture);
      const cylinderClasses: CylinderClass[] = includeMixergy
        ? ['standard', 'mixergy']
        : ['standard'];

      for (const pressure of validPressures) {
        for (const clazz of cylinderClasses) {
          blueprints.push({
            heatSource,
            dhw: {
              architecture: 'stored',
              cylinder: { pressure, clazz, retention: 'replace' },
            },
            controls: { zoning: 'yplan', smart: false },
            distribution: {},
          });
        }
      }
    }
  }

  // Limit if requested
  if (options?.maxOptions && blueprints.length > options.maxOptions) {
    return blueprints.slice(0, options.maxOptions);
  }

  return blueprints;
}

// ============================================================================
// NORMALIZE HELPERS (internal)
// ============================================================================

function normalizeRetrofitValue(val: string | undefined): 'unknown' | 'none' | 'some' | 'good' {
  if (!val) return 'unknown';
  switch (val) {
    case 'none': return 'none';
    case 'some': case 'partial': case 'basic': return 'some';
    case 'good': case 'modern': return 'good';
    default: return 'unknown';
  }
}

function normalizeWallInsulation(val: string | undefined): 'unknown' | 'none' | 'cavity' | 'solid_or_internal_or_external' {
  if (!val) return 'unknown';
  switch (val) {
    case 'none': case 'solid_uninsulated': return 'none';
    case 'cavity': return 'cavity';
    case 'solid_or_internal_or_external': case 'solid_insulated': return 'solid_or_internal_or_external';
    default: return 'unknown';
  }
}

function normalizeWindowValue(val: string | undefined): 'unknown' | 'single' | 'older_double' | 'modern_double_or_triple' {
  if (!val) return 'unknown';
  switch (val) {
    case 'single': return 'single';
    case 'older_double': case 'double': return 'older_double';
    case 'modern_double_or_triple': case 'modern': case 'triple': return 'modern_double_or_triple';
    default: return 'unknown';
  }
}
